/**
 * Pure formatters shared by the tooltip, overlays, and zoom readout (§10.8).
 * Kept in their own module so `overlays.ts` and `tooltip.ts` can both use them
 * without importing each other (they would otherwise form a cycle).
 *
 * All time formatting is UTC: the backend emits naive ISO that is UTC, and the
 * pickers/ticks are UTC, so `parseLegSec` appends `Z` and the `fmtTime*` helpers
 * read via UTC accessors. See [[project_timezone_convention]].
 */

export function formatNum(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return '-';
  const abs = Math.abs(v);
  if (abs !== 0 && (abs >= 1e6 || abs < 1e-3)) return v.toExponential(3);
  if (abs >= 100) return v.toFixed(2);
  if (abs >= 1) return v.toFixed(3);
  return v.toPrecision(4);
}

/** Parse a server timestamp to epoch seconds, forcing UTC for naive ISO. */
export function parseLegSec(s: string | null | undefined): number {
  if (!s) return NaN;
  const hasTz = /Z$|[+-]\d{2}:?\d{2}$/.test(s);
  return new Date(hasTz ? s : s + 'Z').getTime() / 1000;
}

export function fmtTimeFull(sec: number): string {
  if (!Number.isFinite(sec)) return '';
  const d = new Date(sec * 1000);
  const pad = (n: number): string => String(n).padStart(2, '0');
  return (
    `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ` +
    `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}`
  );
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function fmtTimeShort(sec: number): string {
  if (!Number.isFinite(sec)) return '';
  const d = new Date(sec * 1000);
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${pad(d.getUTCDate())} ${MONTHS[d.getUTCMonth()]} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
}

/** `#rrggbb` → `rgba(r,g,b,a)`; pass-through for anything else. */
export function withAlpha(hex: string, alpha: number): string {
  if (!hex || hex[0] !== '#' || hex.length !== 7) return hex;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

/** Minimal HTML escape for the imperative tooltip innerHTML. */
export function escHtml(s: unknown): string {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * A bin width, as short as it can be written without losing it.
 *
 * Steps come off the 1 / 2 / 2.5 / 5 ladder, so they are exact at a handful of
 * decimals - `toPrecision` would render 0.5 as "0.500" and 0.01 as "0.0100".
 */
export function fmtStep(step: number): string {
  if (!Number.isFinite(step) || step <= 0) return '';
  if (step >= 1) return String(Number(step.toFixed(3)));
  const decimals = Math.min(8, Math.max(0, -Math.floor(Math.log10(step)) + 1));
  return String(Number(step.toFixed(decimals)));
}
